// Sandbox tenants (Agent Platform Phase 9A) — provision REAL isolated tenants
// for external developers. Sandbox (kpc_test_…) credentials only work in these
// tenants, and payouts always run on the mock (auto-settling) rail.
import { fmtDate, fmtDateTime } from '../../lib/format';
import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Form, Input, Modal, Space, Table, Tag, Typography, message,
} from 'antd';
import { ExperimentOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  createSandboxTenant, listSandboxTenants, type SandboxTenant,
} from '../../services/sandboxService';

const SandboxTenantsPage: React.FC = () => {
  const [rows, setRows] = useState<SandboxTenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<{ slug: string; adminEmail: string } | null>(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      setRows(await listSandboxTenants());
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not load sandbox tenants.');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    const v = await form.validateFields();
    setSubmitting(true);
    try {
      const out = await createSandboxTenant({
        name: v.name.trim(),
        adminEmail: v.adminEmail.trim().toLowerCase(),
        adminName: v.adminName,
        adminPassword: v.adminPassword,
      });
      setCreated({ slug: out.slug, adminEmail: out.adminEmail });
      setOpen(false);
      form.resetFields();
      load();
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Could not provision the sandbox.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }}>
        <Typography.Title level={4} style={{ margin: 0 }}>
          <ExperimentOutlined /> Sandbox tenants
        </Typography.Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={load} />
          <Button type="primary" icon={<PlusOutlined />} onClick={() => setOpen(true)}>
            Provision sandbox
          </Button>
        </Space>
      </Space>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="A sandbox is a real, isolated tenant for external developers."
        description="Sandbox-environment API credentials (kpc_test_…) only issue tokens inside these tenants — and live credentials are refused here. Payouts always run on the mock auto-settling rail; no live provider is ever reachable."
      />
      {created && (
        <Alert
          type="success"
          closable
          onClose={() => setCreated(null)}
          style={{ marginBottom: 16 }}
          message={`Sandbox provisioned — workspace "${created.slug}"`}
          description={`The developer signs in at the app with workspace ${created.slug} as ${created.adminEmail} using the password you set. Share it over a secure channel.`}
        />
      )}
      <Table<SandboxTenant>
        rowKey="id"
        loading={loading}
        dataSource={rows}
        pagination={false}
        columns={[
          { title: 'Name', dataIndex: 'name' },
          { title: 'Workspace', dataIndex: 'slug', render: (s) => <Typography.Text code>{s}</Typography.Text> },
          { title: 'Admin', dataIndex: 'adminEmail' },
          { title: 'Users', dataIndex: 'users', width: 90 },
          {
            title: 'Status', dataIndex: 'isActive', width: 110,
            render: (a: boolean) => (a ? <Tag color="green">active</Tag> : <Tag>inactive</Tag>),
          },
          {
            title: 'Created', dataIndex: 'createdAt', width: 180,
            render: (v: string) => fmtDateTime(v),
          },
        ]}
      />
      <Modal
        title="Provision a sandbox tenant"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={submit}
        okText="Provision"
        confirmLoading={submitting}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="name" label="Company / project name" rules={[{ required: true, min: 2 }]}>
            <Input placeholder="Acme Integrations" />
          </Form.Item>
          <Form.Item name="adminEmail" label="Developer admin email" rules={[{ required: true, type: 'email' }]}>
            <Input placeholder="dev@acme.example" />
          </Form.Item>
          <Form.Item name="adminName" label="Admin name">
            <Input placeholder="Acme Developer" />
          </Form.Item>
          <Form.Item
            name="adminPassword"
            label="Admin password"
            rules={[{ required: true, min: 10, message: 'At least 10 characters' }]}
            extra="Shared with the developer over a secure channel; they can change it after signing in."
          >
            <Input.Password autoComplete="new-password" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SandboxTenantsPage;
export { SandboxTenantsPage };
